import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { Subject } from 'rxjs';
import { delay, filter, take, takeUntil } from 'rxjs/operators';

import { FuseConfigService } from '@fuse/services/config.service';
import { FuseNavigationService } from '@fuse/components/navigation/navigation.service';
import { FusePerfectScrollbarDirective } from '@fuse/directives/fuse-perfect-scrollbar/fuse-perfect-scrollbar.directive';
import { FuseSidebarService } from '@fuse/components/sidebar/sidebar.service';

import { Pipe } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { SafeHtml3 } from '../../../../../main/owner/safeHtml3.pipe';
import { SafeHtmlU } from '../../../../../main/users/users.component';
import { SafeHtmlMonitoring } from '../../../../../main/monitoring/safeHtml.pipe';
import { SafeHtmlReports } from '../../../../../main/reports/safeHtml.pipe';
import { SafeHtml, LogoutDialogComponent } from '../../../toolbar/toolbar.component';
import { FuseNavigationComponent } from '../../../../../../@fuse/components/navigation/navigation.component';
import { FlexModule } from '@ngbracket/ngx-layout/flex';
import { FusePerfectScrollbarDirective as FusePerfectScrollbarDirective_1 } from '../../../../../../@fuse/directives/fuse-perfect-scrollbar/fuse-perfect-scrollbar.directive';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { ExtendedModule } from '@ngbracket/ngx-layout/extended';
import { NgClass, NgIf } from '@angular/common';

@Pipe({
    name: 'safeHtml',
    standalone: true
})
export class SafeHtml2 {
    constructor(private sanitizer: DomSanitizer) { }

    transform(html) {
        return this.sanitizer.bypassSecurityTrustResourceUrl(html);
    }
}

@Component({
    selector: 'navbar-vertical-style-1',
    templateUrl: './style-1.component.html',
    styleUrls: ['./style-1.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [NgClass, ExtendedModule, MatButtonModule, MatIconModule, MatMenuModule, MatDialogModule, RouterLink, TranslateModule, FusePerfectScrollbarDirective_1, FlexModule, NgIf, FuseNavigationComponent, SafeHtml2, SafeHtml, SafeHtmlReports, SafeHtmlMonitoring, SafeHtmlU, SafeHtml3]
})
export class NavbarVerticalStyle1Component implements OnInit, OnDestroy {
    fuseConfig: any;
    navigation: any;
    opened: Boolean = true;
    triggeredMenu: Boolean = false;
    photo: any = null;

    // Private
    private _fusePerfectScrollbar: FusePerfectScrollbarDirective;
    private _unsubscribeAll: Subject<any>;

    /**
     * Constructor
     */
    constructor(
        private _fuseConfigService: FuseConfigService,
        private _fuseNavigationService: FuseNavigationService,
        private _fuseSidebarService: FuseSidebarService,
        private _router: Router,
        private _dialog: MatDialog
    ) {
        // Set the private defaults
        this._unsubscribeAll = new Subject();
    }

    get darkTheme(): boolean {
        return !!this.fuseConfig?.darkMode;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    // Directive
    @ViewChild(FusePerfectScrollbarDirective, { static: true })
    set directive(theDirective: FusePerfectScrollbarDirective) {
        if (!theDirective) {
            return;
        }

        this._fusePerfectScrollbar = theDirective;

        // Update the scrollbar on collapsable item toggle. 350ms aligns with
        // the M3 emphasized-decelerate open animation (300ms + small buffer).
        // TODO: replace with an `animationend` listener on `.children` to
        // remove the timing coupling.
        this._fuseNavigationService.onItemCollapseToggled
            .pipe(
                delay(350),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe(() => {
                this._fusePerfectScrollbar.update();
            });

        // Scroll to the active item position
        this._router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                take(1)
            )
            .subscribe(() => {
                setTimeout(() => {
                    const activeNavItem: any = document.querySelector('navbar .nav-link.active');

                    if (activeNavItem && activeNavItem.offsetParent) {
                        const activeItemOffsetTop = activeNavItem.offsetTop,
                            activeItemOffsetParentTop = activeNavItem.offsetParent.offsetTop,
                            scrollDistance = activeItemOffsetTop - activeItemOffsetParentTop - (48 * 3) - 168;

                        this._fusePerfectScrollbar.scrollToTop(scrollDistance);
                    }
                });
            }
            );
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {

        this._router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe(() => {
                if (this._fuseSidebarService.getSidebar('navbar')) {
                    this._fuseSidebarService.getSidebar('navbar').close();
                }
            }
            );

        // Subscribe to the config changes
        this._fuseConfigService.config
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((config) => {
                this.fuseConfig = config;
                this.photo = this.fuseConfig.user.photo;
            });

        // Get current navigation
        this._fuseNavigationService.onNavigationChanged
            .pipe(
                filter(value => value !== null),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe(() => {
                this.navigation = this._fuseNavigationService.getCurrentNavigation();
            });


        if (this._router.url.includes('security/control')) {
            this.triggeredMenu = true;
            this.toggleSidebarFolded();
        }
        this._router.events.subscribe((route: any) => {
            if (route instanceof NavigationEnd) {
                // console.log('Navegacion', this.triggeredMenu);
                if (route.url.includes('security/control') && this.triggeredMenu === false) {
                    this.triggeredMenu = true;
                    this.toggleSidebarFolded();
                } else if (route.url.includes('security/control') === false && this.triggeredMenu === true) {
                    this.triggeredMenu = false;
                    this.toggleSidebarFolded();
                }
            }
        });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle sidebar opened status
     */
    toggleSidebarOpened(): void {
        this.opened = true;
        this._fuseSidebarService.getSidebar('navbar').toggleOpen();
    }

    /**
     * Toggle sidebar folded status
     */
    toggleSidebarFolded(): void {
        this.opened = false;
        this._fuseSidebarService.getSidebar('navbar')?.toggleFold();
    }

    /**
     * Toggle dark / light theme — same handler as the toolbar user menu.
     */
    changeTheme(): void {
        const next = !this.darkTheme;
        this._fuseConfigService.setConfig({ darkMode: next });
        this._fuseConfigService.updateUserTheme(next).subscribe();
    }

    /**
     * Open the logout confirmation dialog — reuses the toolbar's dialog.
     */
    logout(): void {
        this._dialog.open(LogoutDialogComponent, { data: {} });
    }
}
